/**
 * ToggleSwitch control for overriding a profile user's photo with a custom URL.
 *
 * Renders a labelled `ToggleSwitch`; when enabled, reveals a `ProfileOveride`
 * text field pre-filled with any existing `photoOverride` URL. Saving or
 * clearing persists the change via `profiles.manage.update`, and toggling off
 * when no override is saved immediately collapses the field without a server
 * round-trip.
 */
import { PadBox } from 'component/basics';
import { ToggleSwitch } from 'component/form';
import { useProfileManage } from 'data/profile';
import { updateProfileManageAsync } from 'data/profiles';
import { useState } from 'react';
import { View } from 'react-native';
import { useDatastore, useInstanceKey } from 'util/datastore';
import { t } from 'util/stdtypes';

import { ProfileOveride } from './profile-override';

export function ProfilePhotoOverride() {
    const instanceKey = useInstanceKey();
    const { profile, isLoading } = useProfileManage(instanceKey);
    if (isLoading) {
        return null;
    }
    return <ProfilePhotoOverrideForm existingOverride={profile?.overridePhotoUrl ?? null} profileKey={instanceKey} />;
}

function ProfilePhotoOverrideForm({ existingOverride, profileKey }: { existingOverride: string | null; profileKey: string }) {
    const datastore = useDatastore();
    const [overrideEnabled, setOverrideEnabled] = useState<boolean>(!!existingOverride);

    async function saveOverrideAsync(value: string) {
        await updateProfileManageAsync(datastore, { profileKey, overridePhotoUrl: value || null });
    }

    async function clearOverrideAsync() {
        await updateProfileManageAsync(datastore, { profileKey, overridePhotoUrl: null });
    }

    async function onToggleAsync(enabled: boolean) {
        setOverrideEnabled(enabled);
        if (!enabled) {
            await clearOverrideAsync();
        }
    }

    return (
        <View>
            <ToggleSwitch
                label={t('profile.edit.override_photo_title', 'Override profile photo')}
                onChange={onToggleAsync}
                spread
                testID='override-profile-photo'
                value={overrideEnabled}
            />
            {overrideEnabled && (
                <PadBox top={16}>
                    <ProfileOveride
                        label={t('profile.edit.photo_override', 'Photo URL')}
                        onCancel={() => {
                            if (!existingOverride) {
                                setOverrideEnabled(false);
                            }
                        }}
                        onSave={saveOverrideAsync}
                        placeholder={t('profile.edit.photo_override.placeholder', 'Add your override url...')}
                        previousValue={existingOverride ?? ''}
                        testID='photo-override-field'
                    />
                </PadBox>
            )}
        </View>
    );
}
